import { useMemo } from 'react'
import { matchPath, PathMatch, useLocation, useSearchParams } from 'react-router-dom'

import { isObject } from 'lodash-es'

import {
  COMMON_MIXPANEL_PAGE,
  COMMON_MIXPANEL_PROPERTIES,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'
import { parentalLeaveUtmCampaignString } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/utm'
import { DirectLinkSource } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/constants'

import { usePreviousRoute } from './usePreviousRoute'
import { routeToPageMap } from '../constants/mixpanelConstants'

/**
 * - Get entry point for mixpanel events
 * - If using this for any view events, make sure to wait until the result is not null.
 * - Make sure there is a mapping of route -> page for your entry point inside `routeToPageMap`
 * @param defaultEntryPoint If there is no previous route, use this as the entry point (eg Direct Link)
 * @returns An object with `entryPoint` prop. `location.state?.mixpanelEntryPoint` takes priority, then `entryPoint` defaults to the previously viewed page.
 * - If `null` - the route is currently changing and entry point is not ready
 * - If `{}` - there is no entry point (no previous route)
 */
export const useDefaultMixpanelEntryPoint = (defaultEntryPoint?: string) => {
  const prevRoute = usePreviousRoute()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const utmCampaignEntryPoint = searchParams.get('utm_campaign')
  const entryPointParam = searchParams.get(COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT)
  const mixpanelEntryPointFromState = location.state?.mixpanelEntryPoint
  const prevRouteMatch = useMemo(
    () =>
      Object.keys(routeToPageMap).find((route) => {
        if (!prevRoute) {
          return null
        }
        return matchPath(route, prevRoute)
      }),
    [prevRoute],
  )
  return useMemo(() => {
    const entryPointFromParam = mixpanelEntryPointFromState || entryPointParam

    // This specific parameter check is necessary as the navigation from the 1Lyra care initial page does not use the LW router.
    // Can be removed when triage is converted to 1Lyra.
    if (entryPointFromParam === DirectLinkSource.CARE_TAB) {
      return { [COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT]: COMMON_MIXPANEL_PAGE.CARE_TAB }
    }
    if (entryPointFromParam) {
      return { [COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT]: entryPointFromParam }
    }

    if (prevRoute === null) {
      return null
    }

    const mixpanelPage = prevRouteMatch
      ? routeToPageMap[isObject(prevRouteMatch) ? (prevRouteMatch as PathMatch)?.pattern?.path : prevRouteMatch]
      : undefined
    if (prevRoute && mixpanelPage) {
      return { [COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT]: mixpanelPage }
    }
    if (Boolean(utmCampaignEntryPoint)) {
      let entryPoint = utmCampaignEntryPoint
      if (utmCampaignEntryPoint?.includes(parentalLeaveUtmCampaignString)) {
        entryPoint = COMMON_MIXPANEL_PAGE.PARENTAL_LEAVE_COACHING
      }
      return { [COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT]: entryPoint }
    }
    if (defaultEntryPoint) {
      return { [COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT]: defaultEntryPoint }
    }

    return {}
  }, [
    mixpanelEntryPointFromState,
    entryPointParam,
    prevRoute,
    prevRouteMatch,
    utmCampaignEntryPoint,
    defaultEntryPoint,
  ])
}
